/*
 * Keeping packs on this device (ToR 1.0(xi), 1.0(xii), 4.1.5): the data key is unwrapped with the
 * device key the server issues (and forgotten when the app closes); a pack is downloaded — the
 * compiled PDF with its page map, the contents, the notes and bookmarks, the papers where the policy
 * allows — sealed and filed with its expiry; a stale copy is refreshed; the outbox is replayed when
 * the network returns. Nothing leaves the store unsealed.
 */
import { apiClient } from '@/api/apiClient';
import type { BoardPackAnnotation, MyBoardPack, MyBoardPackContents, PackBookmark } from '@/api/distribution';
import { fetchMyBoardPackDocument, fetchMyBoardPackPaperBlob } from '@/api/distribution';
import { fromBase64, generateDataKey, importAesKey, unwrapDataKey, wrapDataKey } from './crypto';
import { cachedPolicy, deviceId, pingThisDevice } from './device';
import { dequeue, enqueue, getBlob, getJson, getPackMeta, getWrappedKey, listOutbox, listPacks, outboxCount, putBlob, putJson, putPackMeta, putWrappedKey, removePack, type OutboxItem, type StoredPackMeta } from './packStore';

let dataKey: CryptoKey | null = null;
let keyPromise: Promise<CryptoKey | null> | null = null;

export function forgetDataKey() { dataKey = null; keyPromise = null; }

export function hasDataKey(): boolean { return dataKey !== null; }

/** The device key from the server (memory only), then the data key: unwrapped from the store, or made and wrapped on first use. Null offline with nothing in memory. */
export async function dataKeyAsync(): Promise<CryptoKey | null> {
  if (dataKey) return dataKey;
  keyPromise ??= (async () => {
    try {
      const { data } = await apiClient.get<{ key: string }>(`/api/v1/devices/${deviceId()}/offline-key`);
      const deviceKey = await importAesKey(fromBase64(data.key));
      const wrapped = await getWrappedKey();
      if (wrapped && wrapped.deviceId === deviceId()) {
        try { dataKey = await unwrapDataKey(wrapped.wrapped, deviceKey); return dataKey; } catch { /* sealed under an older device key: start afresh */ }
      }
      const fresh = await generateDataKey();
      await putWrappedKey(await wrapDataKey(fresh, deviceKey), deviceId());
      dataKey = fresh;
      return dataKey;
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 403 || status === 404 || status === 410) {
        // The server will not issue the key: wiped, blocked or offline reading switched off. The store goes dark.
        const { wipeThisDevice } = await import('./device');
        await wipeThisDevice('server', false);
      }
      return null;
    } finally { keyPromise = null; }
  })();
  return keyPromise;
}

export type SyncState = 'synced' | 'stale' | 'downloading' | 'error' | 'absent';

export interface OfflinePackStatus { distributionId: string; state: SyncState; syncedAt: string | null; expiresAt: string | null; error?: string }

const inFlight = new Map<string, Promise<void>>();
const listeners = new Set<() => void>();
export function onOfflineChange(fn: () => void) { listeners.add(fn); return () => { listeners.delete(fn); }; }
function changed() { for (const l of listeners) l(); }

const errors = new Map<string, string>();

export async function offlineStatuses(packs: MyBoardPack[]): Promise<Record<string, OfflinePackStatus>> {
  const out: Record<string, OfflinePackStatus> = {};
  const key = dataKey;
  const stored = key ? await listPacks(key) : [];
  for (const p of packs) {
    const s = stored.find((m) => m.distributionId === p.distributionId);
    const downloading = inFlight.has(p.distributionId);
    out[p.distributionId] = {
      distributionId: p.distributionId,
      state: downloading ? 'downloading' : errors.has(p.distributionId) ? 'error' : !s ? 'absent' : s.versionNo !== p.versionNo || new Date(s.expiresAt) < new Date() ? 'stale' : 'synced',
      syncedAt: s?.syncedAt ?? null, expiresAt: s?.expiresAt ?? null, error: errors.get(p.distributionId),
    };
  }
  return out;
}

/** Downloads (or refreshes) one pack into the store. */
export function syncPack(pack: MyBoardPack): Promise<void> {
  const running = inFlight.get(pack.distributionId);
  if (running) return running;
  const task = (async () => {
    errors.delete(pack.distributionId); changed();
    const key = await dataKeyAsync();
    if (!key) throw new Error('Offline reading is not available on this device.');
    const policy = cachedPolicy() ?? { enabled: true, expiryDays: 14, includePapers: true, maxDaysWithoutContact: 30 };
    if (!policy.enabled) throw new Error('Offline reading is switched off for your organisation.');
    const doc = await fetchMyBoardPackDocument(pack.distributionId, 'CompiledPdf', false);
    await putBlob(key, pack.distributionId, 'pack.pdf', new Uint8Array(await doc.blob.arrayBuffer()));
    const contents = (await apiClient.get<MyBoardPackContents>(`/api/v1/my-board-packs/${pack.distributionId}/contents`)).data;
    await putJson(key, pack.distributionId, 'contents', contents);
    const notes = (await apiClient.get<BoardPackAnnotation[]>(`/api/v1/my-board-packs/${pack.distributionId}/annotations`)).data;
    await putJson(key, pack.distributionId, 'annotations', notes);
    try { const bookmarks = (await apiClient.get<PackBookmark[]>(`/api/v1/my-board-packs/${pack.distributionId}/bookmarks`)).data; await putJson(key, pack.distributionId, 'bookmarks', bookmarks); } catch { /* none */ }
    const papers: StoredPackMeta['papers'] = [];
    for (const paper of contents.sections.flatMap((section) => section.papers ?? [])) {
      let stored = false;
      if (policy.includePapers && !paper.viewOnly) {
        try { const blob = await fetchMyBoardPackPaperBlob(pack.distributionId, paper); await putBlob(key, pack.distributionId, `paper:${paper.documentId}:${paper.versionNo}`, new Uint8Array(await blob.arrayBuffer())); stored = true; } catch { stored = false; }
      }
      papers.push({ documentId: paper.documentId, versionNo: paper.versionNo, title: paper.title, fileName: paper.fileName, stored });
    }
    const now = new Date();
    await putPackMeta(key, {
      distributionId: pack.distributionId, boardPackId: pack.boardPackId, title: pack.title, versionNo: pack.versionNo, periodName: pack.periodName,
      syncedAt: now.toISOString(), expiresAt: new Date(now.getTime() + policy.expiryDays * 86_400_000).toISOString(), pages: doc.pages, papers,
    });
    await pingThisDevice(true);
  })().catch((err: unknown) => { errors.set(pack.distributionId, err instanceof Error ? err.message : String(err)); })
    .finally(() => { inFlight.delete(pack.distributionId); changed(); });
  inFlight.set(pack.distributionId, task); changed();
  return task;
}

export async function removeOfflinePack(distributionId: string): Promise<void> {
  await removePack(distributionId); errors.delete(distributionId); changed();
}

/** Refreshes every stored pack that is stale or whose version moved on; called on sign-in and on return online. */
export async function refreshStale(packs: MyBoardPack[]): Promise<void> {
  const key = dataKey ?? (navigator.onLine ? await dataKeyAsync() : null);
  if (!key) return;
  const stored = await listPacks(key);
  for (const s of stored) {
    const live = packs.find((p) => p.distributionId === s.distributionId);
    if (!live) { await removePack(s.distributionId); continue; }
    if (live.versionNo !== s.versionNo || new Date(s.expiresAt) < new Date()) await syncPack(live);
  }
  changed();
}

/** Drops copies past their expiry when the server cannot be asked (ToR 4.3.1). */
export async function dropExpired(): Promise<void> {
  const key = dataKey;
  if (!key) return;
  for (const s of await listPacks(key)) if (new Date(s.expiresAt) < new Date()) await removePack(s.distributionId);
}

// ----------------------------- Reading from the store -----------------------------

export async function storedPack(distributionId: string): Promise<{ meta: StoredPackMeta; pdf: Blob } | null> {
  const key = dataKey;
  if (!key) return null;
  const meta = await getPackMeta(key, distributionId);
  const bytes = meta ? await getBlob(key, distributionId, 'pack.pdf') : null;
  return meta && bytes ? { meta, pdf: new Blob([bytes], { type: 'application/pdf' }) } : null;
}

export async function storedJson<T>(distributionId: string, name: 'contents' | 'annotations' | 'bookmarks'): Promise<T | null> {
  const key = dataKey;
  return key ? getJson<T>(key, distributionId, name) : null;
}

export async function storedPaper(distributionId: string, documentId: string, versionNo: number): Promise<Blob | null> {
  const key = dataKey;
  if (!key) return null;
  const bytes = await getBlob(key, distributionId, `paper:${documentId}:${versionNo}`);
  return bytes ? new Blob([bytes]) : null;
}

export async function storedList(): Promise<StoredPackMeta[]> {
  return dataKey ? listPacks(dataKey) : [];
}

// ----------------------------- The outbox -----------------------------

export async function queue(item: Omit<OutboxItem, 'id' | 'queuedAt' | 'attempts'>): Promise<OutboxItem | null> {
  const key = dataKey;
  if (!key) return null;
  const full: OutboxItem = { ...item, id: crypto.randomUUID(), queuedAt: new Date().toISOString(), attempts: 0 };
  await enqueue(key, full); changed();
  return full;
}

export async function pendingCount(): Promise<number> { return outboxCount(); }

/** Replays what was done offline, oldest first; the server wins (a note with a client id is added once). */
export async function replayOutbox(): Promise<{ sent: number; failed: number }> {
  const key = dataKey ?? await dataKeyAsync();
  if (!key || !navigator.onLine) return { sent: 0, failed: 0 };
  let sent = 0, failed = 0;
  for (const item of await listOutbox(key)) {
    try {
      switch (item.kind) {
        case 'annotation': await apiClient.post(`/api/v1/my-board-packs/${item.distributionId}/annotations`, item.body); break;
        case 'readEvent': { const b = item.body as { kind: string; key: string }; await apiClient.post(`/api/v1/my-board-packs/${item.distributionId}/events`, b); break; }
        case 'readingPosition': await apiClient.put(`/api/v1/my-board-packs/${item.distributionId}/reading-position`, item.body); break;
        case 'acknowledge': await apiClient.post(`/api/v1/my-board-packs/${item.distributionId}/acknowledge`); break;
      }
      await dequeue(item.id); sent++;
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      // A refusal the server will repeat (gone, forbidden, invalid) is dropped; a network failure waits for the next try.
      if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) { await dequeue(item.id); failed++; }
      else { failed++; break; }
    }
  }
  changed();
  return { sent, failed };
}
